import { Card, Collapse, Avatar } from 'antd'
import { CheckCircleOutlined, DownOutlined } from '@ant-design/icons'
import { TopLeadSourceData } from './data'
import { mockTopLeadSourceData } from './data'
import useGetAnalyticsData from '@forwardflow/hooks/customHooks/marketing/useGetAnalyticsData'

const { Panel } = Collapse

interface TopLeadSourceProps {
   data?: TopLeadSourceData
}

const TopLeadSource = ({
   data = mockTopLeadSourceData,
}: TopLeadSourceProps) => {
   const { data: analyticsData } = useGetAnalyticsData()

   const getInitials = (fullName: string) => {
      if (!fullName) return 'N/A'
      return fullName
         .split(' ')
         .map((name) => name.charAt(0))
         .join('')
         .toUpperCase()
   }

   const getBestLeadSource = (leadSources: any[]) => {
      if (!leadSources || leadSources.length === 0) return null
      
      const validSources = leadSources.filter(source => 
         source.totalLeads && source.totalLeads > 0
      )
      
      if (validSources.length === 0) return null
      
      const sourcesWithRates = validSources.map(source => ({
         ...source,
         conversionRate: (source.appointments / source.totalLeads) * 100
      }))
      
      return sourcesWithRates.reduce((prev, current) =>
         prev.conversionRate > current.conversionRate ? prev : current
      )
   }

   // Helper function to find the best outreach effort agent
   const getBestOutreachAgent = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.avgContactAttemptsPerLead && agent.avgContactAttemptsPerLead > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.avgContactAttemptsPerLead > current.avgContactAttemptsPerLead ? prev : current
      )
   }

   // Helper function to find the best conversationalist
   const getBestConversationalist = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.avgConversationsPerLead && agent.avgConversationsPerLead > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.avgConversationsPerLead < current.avgConversationsPerLead ? prev : current
      )
   }

   // Helper function to find the top appointment setter
   const getTopAppointmentSetter = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.appointmentSetRatePercent && agent.appointmentSetRatePercent > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.appointmentSetRatePercent > current.appointmentSetRatePercent ? prev : current
      )
   }

   // Helper function to find the best text response rate agent
   const getBestTextResponseAgent = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.textResponseRate && agent.textResponseRate > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.textResponseRate > current.textResponseRate ? prev : current
      )
   }

   const bestLeadSource = getBestLeadSource(analyticsData?.bestLeadSources)
   const bestOutreachAgent = getBestOutreachAgent(analyticsData?.agentStats)
   const bestConversationalist = getBestConversationalist(analyticsData?.agentStats)
   const topAppointmentSetter = getTopAppointmentSetter(analyticsData?.agentStats)
   const bestTextResponseAgent = getBestTextResponseAgent(analyticsData?.agentStats)

   // Calculate grid columns dynamically based on available cards
   const agentCards = [bestOutreachAgent, bestConversationalist, topAppointmentSetter, bestTextResponseAgent].filter(Boolean)

   const getGridClass = (cardCount: number) => {
      if (cardCount === 0) return 'hidden'
      if (cardCount === 1) return 'grid grid-cols-1 gap-4 mb-6'
      if (cardCount === 2) return 'grid grid-cols-1 gap-4 mb-6 md:grid-cols-2'
      if (cardCount === 3) return 'grid grid-cols-1 gap-4 mb-6 md:grid-cols-3'
      return 'grid grid-cols-1 gap-4 mb-6 md:grid-cols-2'
   }

   // Check if we have any meaningful data to display
   const hasAnyData = bestLeadSource || agentCards.length > 0

   return (
      <div className="w-full mx-auto">
         <style>{`
            :global(.collapse-dark .ant-collapse-arrow) {
               color: white !important;
            }
            :global(.collapse-dark .ant-collapse-expand-icon) {
               color: white !important;
            }
         `}</style>

         {/* Header */}
         <div className="px-6 py-4 mb-4 text-white rounded-lg bg-primary-dark">
            <div className="flex items-center gap-4">
               <div className="flex items-center justify-center w-12 h-12">
                  <div className="relative">
                     <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                        <div className="flex items-center justify-center w-8 h-8 bg-white rounded-md">
                           <div className="text-lg">📊</div>
                        </div>
                     </div>
                  </div>
               </div>
               <h2 className="text-lg font-medium">TOP LEAD SOURCE</h2>
            </div>
         </div>

         {/* Cards Container */}
         <div className="rounded-xl backdrop-blur-sm">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
               {/* Left Column - Pro Tip Card */}
               <div className="lg:col-span-1">
                  <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 h-full transition-all duration-200 group hover:shadow-xl">
                     <div className="text-center">
                        <div className="mb-4">
                           <h3 className="text-sm font-semibold text-yellow-400">
                              {data.proTip.title}
                           </h3>
                        </div>

                        <div className="flex items-center justify-center h-32 mb-4">
                           <div className="relative">
                              <div className="flex items-center justify-center w-20 h-20 rounded-lg shadow-lg bg-gradient-to-br from-yellow-500 to-yellow-600">
                                 <div className="flex items-center justify-center w-16 h-16 bg-white rounded-lg">
                                    <div className="text-3xl">
                                       {data.proTip.icon}
                                    </div>
                                 </div>
                              </div>
                              <div className="absolute flex items-center justify-center w-6 h-6 bg-yellow-600 rounded-full shadow-md -bottom-1 -right-1">
                                 <span className="text-xs text-white">✨</span>
                              </div>
                           </div>
                        </div>

                        <p className="text-sm leading-relaxed text-gray-300">
                           {data.proTip.message}
                        </p>
                     </div>
                  </Card>
               </div>

               {/* Right Column - Main Content */}
               <div className="space-y-6 lg:col-span-2">
                  {/* If no data, show message */}
                  {!hasAnyData ? (
                     <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200">
                        <div className="py-12 text-center">
                           <div className="mb-4">
                              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-600 rounded-full opacity-50">
                                 <span className="text-2xl">📊</span>
                              </div>
                           </div>
                           <h3 className="mb-2 text-lg font-semibold text-white">No Lead Source Data Available</h3>
                           <p className="text-gray-400">
                              Lead source performance data will appear here once you start tracking appointments and conversions.
                           </p>
                        </div>
                     </Card>
                  ) : (
                     <>
                        {/* Lead Source Card - Only show if bestLeadSource exists */}
                        {bestLeadSource && (
                           <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200 group hover:shadow-xl">
                              <Collapse
                                 defaultActiveKey={['1']}
                                 ghost
                                 expandIcon={({ isActive }) => (
                                    <DownOutlined
                                       rotate={isActive ? 180 : 0}
                                       className="!text-white"
                                    />
                                 )}
                                 className="collapse-dark"
                              >
                                 <Panel
                                    header={
                                       <div className="flex items-center justify-between w-full pr-4">
                                          <span className="font-semibold text-white">
                                             Which lead source is providing the best
                                             leads?
                                          </span>
                                          <div className="px-3 py-1 text-sm text-green-400 border rounded-full bg-green-500/20 border-green-500/30">
                                             {bestLeadSource.leadSource} is currently the best!
                                          </div>
                                       </div>
                                    }
                                    key="1"
                                    className="border-none"
                                 >
                                    <div className="pt-4">
                                       <div className="flex items-start gap-3 mb-6">
                                          <div className="flex items-center justify-center w-8 h-8 mt-1 bg-green-500 rounded-full shrink-0">
                                             <CheckCircleOutlined className="text-sm text-white" />
                                          </div>
                                          <p className="text-gray-300">
                                             {bestLeadSource.leadSource} has provided you with
                                             the most appointment leads for this time period -{' '}
                                             {bestLeadSource.appointments} out of{' '}
                                             {bestLeadSource.totalLeads}{' '}
                                             total{' '}
                                             <strong className="text-white">
                                                ({bestLeadSource.conversionRate.toFixed(1)}%)
                                             </strong>
                                             .
                                          </p>
                                       </div>
                                    </div>
                                 </Panel>
                              </Collapse>
                           </Card>
                        )}

                        {/* Agent Performance Section - Only show if there are valid agent cards */}
                        {agentCards.length > 0 && (
                           <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200 group hover:shadow-xl">
                              <Collapse
                                 defaultActiveKey={agentCards.length > 0 ? ['1'] : []}
                                 ghost
                                 expandIcon={({ isActive }) => (
                                    <DownOutlined
                                       rotate={isActive ? 180 : 0}
                                       className="!text-white"
                                    />
                                 )}
                                 className="collapse-dark"
                              >
                                 <Panel
                                    header={
                                       <span className="font-semibold text-white">
                                          Who are your best agents{bestLeadSource ? ` on ${bestLeadSource.leadSource}` : ''}?
                                       </span>
                                    }
                                    key="1"
                                    className="border-none"
                                 >
                                    <div className="pt-4">
                                       {/* Agent Performance Cards Grid */}
                                       <div className={getGridClass(agentCards.length)}>
                                          {/* Best Outreach Effort */}
                                          {bestOutreachAgent && (
                                             <Card className="bg-gradient-to-br from-[#323447] to-[#2a2d3e] border-[#3a3548] border">
                                                <div className="flex items-center justify-between">
                                                   <div>
                                                      <h4 className="mb-1 text-sm font-semibold text-gray-300">
                                                         Best Outreach Effort
                                                      </h4>
                                                      <p className="mb-1 text-lg font-bold text-white">
                                                         {bestOutreachAgent.agentName}
                                                      </p>
                                                      <p className="text-sm text-green-400">
                                                         {bestOutreachAgent.avgContactAttemptsPerLead}{' '}
                                                         average contact attempts per lead
                                                      </p>
                                                   </div>
                                                   <div className="flex items-center">
                                                      <div className="flex items-center justify-center w-8 h-8 mr-2 bg-green-500 rounded-full">
                                                         <CheckCircleOutlined className="text-sm text-white" />
                                                      </div>
                                                      <Avatar className="text-white bg-gray-600">
                                                         {getInitials(bestOutreachAgent.agentName)}
                                                      </Avatar>
                                                   </div>
                                                </div>
                                             </Card>
                                          )}

                                          {/* Best Conversationalist */}
                                          {bestConversationalist && (
                                             <Card className="bg-gradient-to-br from-[#323447] to-[#2a2d3e] border-[#3a3548] border">
                                                <div className="flex items-center justify-between">
                                                   <div>
                                                      <h4 className="mb-1 text-sm font-semibold text-gray-300">
                                                         Best Conversationalist
                                                      </h4>
                                                      <p className="mb-1 text-lg font-bold text-white">
                                                         {bestConversationalist.agentName}
                                                      </p>
                                                      <p className="text-sm text-green-400">
                                                         {bestConversationalist.avgConversationsPerLead}{' '}
                                                         average conversations per lead
                                                      </p>
                                                   </div>
                                                   <div className="flex items-center">
                                                      <div className="flex items-center justify-center w-8 h-8 mr-2 bg-green-500 rounded-full">
                                                         <CheckCircleOutlined className="text-sm text-white" />
                                                      </div>
                                                      <Avatar className="text-white bg-gray-600">
                                                         {getInitials(bestConversationalist.agentName)}
                                                      </Avatar>
                                                   </div>
                                                </div>
                                             </Card>
                                          )}

                                          {/* Top Appointment Setter */}
                                          {topAppointmentSetter && (
                                             <Card className="bg-gradient-to-br from-[#323447] to-[#2a2d3e] border-[#3a3548] border">
                                                <div className="flex items-center justify-between">
                                                   <div>
                                                      <h4 className="mb-1 text-sm font-semibold text-gray-300">
                                                         Top Appointment Setter
                                                      </h4>
                                                      <p className="mb-1 text-lg font-bold text-white">
                                                         {topAppointmentSetter.agentName}
                                                      </p>
                                                      <p className="text-sm text-green-400">
                                                         Appointment set rate:{' '}
                                                         {topAppointmentSetter.appointmentSetRatePercent}%
                                                      </p>
                                                   </div>
                                                   <div className="flex items-center">
                                                      <div className="flex items-center justify-center w-8 h-8 mr-2 bg-green-500 rounded-full">
                                                         <CheckCircleOutlined className="text-sm text-white" />
                                                      </div>
                                                      <Avatar className="text-white bg-gray-600">
                                                         {getInitials(topAppointmentSetter.agentName)}
                                                      </Avatar>
                                                   </div>
                                                </div>
                                             </Card>
                                          )}

                                          {/* Best Text Response Rate */}
                                          {bestTextResponseAgent && (
                                             <Card className="bg-gradient-to-br from-[#323447] to-[#2a2d3e] border-[#3a3548] border">
                                                <div className="flex items-center justify-between">
                                                   <div>
                                                      <h4 className="mb-1 text-sm font-semibold text-gray-300">
                                                         Best Text Response Rate
                                                      </h4>
                                                      <p className="mb-1 text-lg font-bold text-white">
                                                         {bestTextResponseAgent.agentName}
                                                      </p>
                                                      <p className="text-sm text-green-400">
                                                         Leads respond{' '}
                                                         {(bestTextResponseAgent.textResponseRate * 100).toFixed(1)}%{' '}
                                                         of the time
                                                      </p>
                                                   </div>
                                                   <div className="flex items-center">
                                                      <div className="flex items-center justify-center w-8 h-8 mr-2 bg-green-500 rounded-full">
                                                         <CheckCircleOutlined className="text-sm text-white" />
                                                      </div>
                                                      <Avatar className="text-white bg-gray-600">
                                                         {getInitials(bestTextResponseAgent.agentName)}
                                                      </Avatar>
                                                   </div>
                                                </div>
                                             </Card>
                                          )}
                                       </div>
                                    </div>
                                 </Panel>
                              </Collapse>
                           </Card>
                        )}
                     </>
                  )}
               </div>
            </div>
         </div>
      </div>
   )
}

export default TopLeadSource